/**
 * EventLog — Chronological event list with type icons.
 */

import type { Event } from '../api/client';

interface EventLogProps {
  events: Event[];
}

const TYPE_LABELS: Record<string, string> = {
  payment: 'Payment',
  change_dispensed: 'Change',
  printer_online: 'Printer Online',
  printer_offline: 'Printer Offline',
  error: 'Error',
  // OPS-16: boot + cashbox/connectivity machine events also arrive here.
  machine_boot: 'Boot',
  boot: 'Boot',
  cash_inserted: 'Cash In',
  cashbox_alert: 'Cashbox Alert',
  cashbox_full: 'Cashbox Full',
  connectivity_changed: 'Connectivity',
  change_shortfall: 'Change Shortfall',
  admin_action: 'Admin Action',
};

/**
 * Human-readable detail line. Prefers the stored `message`, but falls back to
 * the `details` payload — `admin_action` events (paper_ok / cashout / checkin)
 * carry the specific action and amount there, not in `message`, so without this
 * the whole service menu shows up as an indistinguishable "Admin Action".
 */
function describeEvent(event: Event): string {
  if (event.message) return event.message;
  const d = event.details ?? {};
  if (event.type === 'admin_action') {
    const action = typeof d.action === 'string' ? d.action : '';
    switch (action) {
      case 'paper_ok': {
        const bw = typeof d.paperFullSheetsBw === 'number' ? d.paperFullSheetsBw : null;
        const col = typeof d.paperFullSheetsColor === 'number' ? d.paperFullSheetsColor : null;
        if (bw != null || col != null)
          return `Paper refilled — B/W ${bw ?? '?'}, color ${col ?? '?'}`;
        return 'Paper refilled — all trays';
      }
      case 'cashout': {
        const kc = typeof d.amountCenti === 'number' ? Math.round(d.amountCenti / 100) : null;
        return kc != null ? `Cash removed — ${kc} Kč` : 'Cash removed';
      }
      case 'checkin': {
        const coins = (['c1', 'c2', 'c5', 'c10', 'c20'] as const)
          .map((k) => [k.slice(1), Number(d[k] ?? 0)] as const)
          .filter(([, n]) => n > 0)
          .map(([v, n]) => `${v} Kč×${n}`);
        return coins.length ? `Coin refill — ${coins.join(', ')}` : 'Coin refill';
      }
      case 'reboot':
        return 'App restart';
      case 'open_windows':
        return 'Windows desktop';
      case 'exit':
        return 'Close service menu';
      default:
        return action ? `Admin: ${action}` : 'Admin action';
    }
  }
  return '';
}

export function EventLog({ events }: EventLogProps) {
  if (events.length === 0) {
    return (
      <div className="empty-state">
        <p className="text-muted">No events to display.</p>
      </div>
    );
  }

  return (
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Time</th>
            <th>Type</th>
            <th>Machine</th>
            <th>Message</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr key={event.id}>
              <td style={{ whiteSpace: 'nowrap' }}>
                {new Date(event.timestamp).toLocaleString()}
              </td>
              <td>
                <span className="badge badge--online" style={{ fontSize: '0.75rem' }}>
                  {TYPE_LABELS[event.type] ?? event.type}
                </span>
              </td>
              <td>#{event.machineId}</td>
              <td>{describeEvent(event)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
